/**
 * Persistence boundary for per-feature Heidi Labs opt-ins (read side).
 *
 * A user can opt into (or out of) an individual experimental feature from the
 * Labs settings page. That choice is persisted on the user's
 * `session_settings.labs_feature_overrides` map (backend), keyed by a stable
 * {@link LabsFeatureId}:
 *   - `true`      -> force the feature on for this user
 *   - `false`     -> force the feature off for this user
 *   - *(absent)*  -> follow the underlying feature flag (rollout / targeting)
 *
 * This is the per-user analogue of `opted_in_experimental`: it rides on the
 * standard `/user/` auth, so the choice roams across devices and survives a
 * cache clear. See `FE_LABS_CONTEXT.md` for the backend contract.
 *
 * The feature-flag hooks (`useIsSessionPageV2Enabled`, …) resolve the effective
 * value via {@link resolveLabsFeature} — resolution lives in the hooks, NOT in
 * the OpenFeature provider, so the production provider stays untouched.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * This module is a LEAF. It reads the cached user via `useCurrentUserQueryResult`
 * (not `useQueryUser`, which pulls in `@/features/flags` and would create a
 * cycle: flags → labs-overrides → useQueryUser → flags). The WRITE hook lives in
 * `use-set-labs-feature-opt-in.ts` so `useMutateUpdateUser`'s heavy module graph
 * is never loaded on the flag-read path. Do NOT import `@/features/flags` here.
 * ───────────────────────────────────────────────────────────────────────────
 */
import { useCurrentUserQueryResult } from '@/context/current-user-context';
import { type SessionSettingsResponseWithHighlights } from '@heidi/workspace/contracts';

import { type LabsFeatureId } from './labs-feature-ids';

export { LABS_FEATURE, type LabsFeatureId } from './labs-feature-ids';

/** Map of feature id -> explicit opt-in. Absent key means "follow the flag". */
export type LabsOverrideMap = Readonly<Partial<Record<LabsFeatureId, boolean>>>;

/** Stable empty reference so consumers can index without null checks. */
export const EMPTY_LABS_OVERRIDES: LabsOverrideMap = Object.freeze({});

/**
 * Tri-state resolution: an explicit opt-in wins over the flag; otherwise follow
 * the flag's own evaluation. Used by the feature-flag hooks.
 */
export const resolveLabsFeature = (
  optIn: boolean | undefined,
  flagValue: boolean
): boolean => optIn ?? flagValue;

/**
 * Reactive read of the whole override map from the cached user. Returns a
 * stable empty object when the user has no stored overrides (or before the user
 * query resolves), so callers can index it safely and unknown features fall
 * back to "follow the flag".
 */
export const useLabsOverrides = (): LabsOverrideMap => {
  const { data } = useCurrentUserQueryResult(
    (user) =>
      // `labs_feature_overrides` isn't in the generated schema yet; read it via
      // the local extension type, mirroring useHighlightPreferencesSync.
      // TODO: drop the cast after pnpm schema is re-run against deployed BE.
      ((user.session_settings as SessionSettingsResponseWithHighlights | null)
        ?.labs_feature_overrides as LabsOverrideMap | undefined) ?? EMPTY_LABS_OVERRIDES
  );
  return data ?? EMPTY_LABS_OVERRIDES;
};

/**
 * Reactive read of a single feature's opt-in. Returns `undefined` when the user
 * has not made an explicit choice (i.e. follow the flag).
 */
export const useLabsFeatureOptIn = (id: LabsFeatureId): boolean | undefined => {
  return useLabsOverrides()[id];
};

/**
 * Reactive read of the read-only auto-enrolment stamp
 * (`scribe_session_page_v2_enabled`). `true` once the backend auto-enrolled the
 * user into V2 at account creation. The render hook ORs this with the Labs
 * opt-in (stamp wins); the Labs card is hidden while it is true.
 */
export const useIsAutoEnrolledSessionLayoutV2 = (): boolean => {
  const { data } = useCurrentUserQueryResult(
    (user) => user.scribe_session_page_v2_enabled === true
  );
  return data ?? false;
};
